import React, { useEffect, useState } from "react";
import "./ManageBranch.scss";

import { FaPen, FaTrash } from "react-icons/fa";

import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../../constants/routes";
import { useForm } from "react-hook-form";
import CustomButton from "../../../components/CustomButton/CustomButton";
import InputField from "../../../components/InputField/InputField";
import Loader from "../../../components/Loader/Loader";
import { fetchBranchesByOrganization } from "../../../api/organization";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import { addBranch, deleteBranch, updateBranch } from "../../../api/branches";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  updateActiveBranch,
  updateBranches,
} from "../../../redux/features/branch-slice";
import { updateOpenSubscriptionAd } from "../../../redux/features/subscription-slice";

const ManageBranch = () => {
  const { id: original_organization_id } = useSelector(
    (state) => state.organizationReducer.value
  );
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const organization_id = location.state?.organization_id || null;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({});

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  useEffect(()=>{
    navigate(ROUTES.myOrganization.path);
  },[])

  const getBranches = () => {
    setLoading(true);
    fetchBranchesByOrganization(organization_id)
      .then((res) => {
        const branchesList = res.data;
        if (branchesList?.length > 0) {
          setBranches(branchesList);
          if (original_organization_id === organization_id) {
            dispatch(updateBranches({ branches: branchesList }));
            dispatch(updateActiveBranch(branchesList[0]));
          }
        } else {
          setBranches([]);
          if (original_organization_id === organization_id) {
            dispatch(updateBranches({ branches: [] }));
            dispatch(updateActiveBranch({}));
          }
        }
      })
      .catch((err) => {
        console.log("Error while fetching branches!", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!organization_id) {
      navigate(ROUTES.myOrganization.path);
    } else {
      getBranches();
    }
  }, [organization_id]);

  const handleAddBranch = (data) => {
    const branchData = {
      name: data?.name,
      address: data?.address,
      phone: data?.phone,
    };

    setSubmitting(true);
    addBranch(organization_id, branchData)
      .then((res) => {
        toast.dismiss(); toast.success("New branch added successfully.");
        getBranches();
        reset({
          name: "",
          address: "",
          phone: "",
        });
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          if (err?.response?.data?.message === "User not authorized!") {
            toast.dismiss(); toast.error(
              "You do not have sufficient permissions to perform this action. Please contact your administrator."
            );
          } else {
            toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
          }
        } else {
          toast.dismiss(); toast.error("Error while adding new branch!");
        }
        if(err?.response?.data?.upgrade){
          dispatch(updateOpenSubscriptionAd({openAd: true}));
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleUpdateBranch = (data) => {
    const branchData = {
      name: data?.name,
      address: data?.address,
      phone: data?.phone,
    };
    if (selectedBranch?.id) {
      setSubmitting(true);
      updateBranch(organization_id, selectedBranch?.id, branchData)
        .then((res) => {
          toast.dismiss(); toast.success("Branch updated successfully.");
          setOpenUpdate(false);
          getBranches();
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss(); toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss(); toast.error("Error while updating branch!");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const handleDeleteBranch = () => {
    if (selectedBranch?.id) {
      setDeleting(true);
      deleteBranch(organization_id, selectedBranch?.id)
        .then((res) => {
          toast.dismiss(); toast.success("Branch deleted successfully.");
          setOpenDelete(false);
          getBranches();
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss(); toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss(); toast.error("Error while deleting branch!");
          }
        })
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="manage-branch">
        <form
          className="container-wrapper add-form"
          onSubmit={handleSubmit(handleAddBranch)}
        >
          <div className="form-container">
            <InputField
              name={"name"}
              type={"text"}
              label={"Branch Name*"}
              required={true}
              register={register}
              inputErrors={errors}
              watch={watch}
            />

            <InputField
              name={"address"}
              type={"text"}
              label={"Address*"}
              required={true}
              register={register}
              inputErrors={errors}
              watch={watch}
            />

            <InputField
              name={"phone"}
              type={"number"}
              label={"Phone"}
              required={false}
              register={register}
              inputErrors={errors}
              watch={watch}
            />

            <div className="buttons">
              <CustomButton
                name={"Add Branch"}
                color={"primary"}
                type={"submit"}
                handleSubmit={() => {}}
                disabled={submitting}
                bigButton={true}
                loader={submitting}
              />
            </div>
          </div>
        </form>

        <div className="branch-list container-wrapper">
          {branches.map((el, i) => {
            return (
              <div className="item" key={i}>
                <div className="left">
                  <p>{el?.name}</p>
                  <span>
                    {el?.address || "No Address"} - {el?.phone || "No Phone"}
                  </span>
                </div>

                <div className="right">
                  <FaPen
                    onClick={() => {
                      setSelectedBranch(el);
                      setOpenUpdate(true);
                    }}
                  />
                  {el?.name !== "Main branch" && (
                    <FaTrash
                      onClick={() => {
                        setSelectedBranch(el);
                        setOpenDelete(true);
                      }}
                      className="danger"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DeleteModal
          deleting={deleting}
          open={openDelete}
          setOpen={setOpenDelete}
          handleDelete={handleDeleteBranch}
          name={`${selectedBranch?.name} Branch`}
        />

        <EditBranch
          open={openUpdate}
          setOpen={setOpenUpdate}
          selectedBranch={selectedBranch}
          handleUpdateBranch={handleUpdateBranch}
          submitting={submitting}
        />
      </div>
    </>
  );
};

export default ManageBranch;

const EditBranch = ({
  open,
  setOpen,
  selectedBranch,
  handleUpdateBranch,
  submitting,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({});

  useEffect(() => {
    reset({
      name: selectedBranch?.name,
      address: selectedBranch?.address,
      phone: selectedBranch?.phone,
    });
  }, [selectedBranch, open]);
  return (
    <>
      <div className={`edit-modal ${open ? "active" : ""}`}>
        <p className="heading">Edit {selectedBranch?.name} Branch</p>
        <form onSubmit={handleSubmit(handleUpdateBranch)}>
          <div className="form-content">
            <InputField
              name={"name"}
              type={"text"}
              label={"Branch Name*"}
              required={true}
              register={register}
              inputErrors={errors}
              watch={watch}
              disabled={selectedBranch?.name === "Main branch"}
            />

            <InputField
              name={"address"}
              type={"text"}
              label={"Address*"}
              required={true}
              register={register}
              inputErrors={errors}
              watch={watch}
            />

            <InputField
              name={"phone"}
              type={"number"}
              label={"Phone"}
              required={false}
              register={register}
              inputErrors={errors}
              watch={watch}
            />
          </div>

          <div className="buttons">
            <CustomButton
              name={"Cancel"}
              color={"danger"}
              type={"button"}
              handleSubmit={() => {
                setOpen(false);
              }}
              disabled={submitting}
            />
            <CustomButton
              name={"Update"}
              color={"primary"}
              type={"submit"}
              handleSubmit={() => {}}
              disabled={submitting}
              loader={submitting}
            />
          </div>
        </form>
      </div>
      <div
        className={`back-delete-modal ${open ? "active" : ""}`}
        onClick={() => {
          setOpen(false);
        }}
      ></div>
    </>
  );
};
